/**
 * Idle session timeout duration in milliseconds.
 * Read from REACT_APP_IDLE_TIMEOUT_MS in the active env file
 * (120000 = 2 min in .env.development, 14400000 = 4 hrs in .env.production).
 */
export const IDLE_TIMEOUT_MS = parseInt(
  process.env.REACT_APP_IDLE_TIMEOUT_MS || '',
  10
);

/** localStorage key holding the idle expiry timestamp (epoch ms) */
export const IDLE_EXPIRY_KEY = 'idle_expiry';

/**
 * Authentication utility functions for handling user authentication state and tokens
 */
export const authUtils = {
  /**
   * Checks if a user is currently authenticated by verifying the presence of an auth token
   * @returns {boolean} True if a valid auth token exists, false otherwise
   */
  isAuthenticated: (): boolean => {
    try {
      const token = localStorage.getItem('auth_token');
      return !!token; // Return true if token exists
    } catch (error) {
      console.error('Error checking authentication:', error);
      return false;
    }
  },

  /**
   * Retrieves the authentication token from localStorage
   * @returns {string | null} The authentication token if it exists, null otherwise
   */
  getToken: (): string | null => {
    try {
      return localStorage.getItem('auth_token');
    } catch (error) {
      console.error('Error getting token:', error);
      return null;
    }
  },

  /**
   * Stores the authentication token in localStorage
   * @param {string} token - The authentication token to store
   * @returns {void}
   */
  setToken: (token: string): void => {
    try {
      localStorage.setItem('auth_token', token);
    } catch (error) {
      console.error('Error setting token:', error);
    }
  },

  /**
   * Removes the authentication token from localStorage
   * @returns {void}
   */
  removeToken: (): void => {
    try {
      localStorage.removeItem('auth_token');
    } catch (error) {
      console.error('Error removing token:', error);
    }
  },

  /**
   * Refreshes the idle expiry timestamp to now + IDLE_TIMEOUT_MS.
   * Called on any user activity. Because the value lives in localStorage,
   * all open tabs share the same deadline.
   * @returns {void}
   */
  refreshIdleExpiry: (): void => {
    try {
      localStorage.setItem(IDLE_EXPIRY_KEY, String(Date.now() + IDLE_TIMEOUT_MS));
    } catch (error) {
      console.error('Error refreshing idle expiry:', error);
    }
  },

  /**
   * Returns the stored idle expiry timestamp, or null if none exists.
   * @returns {number | null} Expiry time in epoch milliseconds
   */
  getIdleExpiry: (): number | null => {
    try {
      const raw = localStorage.getItem(IDLE_EXPIRY_KEY);
      if (!raw) return null;
      const ts = Number(raw);
      return Number.isFinite(ts) ? ts : null;
    } catch (error) {
      console.error('Error reading idle expiry:', error);
      return null;
    }
  },

  /**
   * Checks whether the idle session has expired.
   * @returns {boolean} True if the stored expiry time has passed
   */
  isIdleExpired: (): boolean => {
    const expiry = authUtils.getIdleExpiry();
    return expiry !== null && Date.now() >= expiry;
  },

  /**
   * Removes the idle expiry timestamp from localStorage
   * @returns {void}
   */
  clearIdleExpiry: (): void => {
    try {
      localStorage.removeItem(IDLE_EXPIRY_KEY);
    } catch (error) {
      console.error('Error clearing idle expiry:', error);
    }
  }
};

/**
 * Extracts clean text content from HTML by removing all HTML tags, comments, and scripts
 * @param {string} html - The HTML string to process
 * @returns {string} The cleaned text content with normalized whitespace
 * @throws {Error} If there's an error during HTML parsing
 * @example
 * const text = extractTextFromHtml('<div>Hello <b>World</b>!</div>');
 * // Returns: 'Hello World!'
 */
export const extractTextFromHtml = (html: string): string => {
  if (!html || typeof html !== 'string') return '';

  try {
    // Remove HTML comments first
    let cleanHtml = html.replace(/<!--[\s\S]*?-->/g, '');

    // Remove style tags and their content
    cleanHtml = cleanHtml.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '');

    // Remove script tags and their content
    cleanHtml = cleanHtml.replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '');

    // Create a temporary DOM element to parse HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = cleanHtml;

    // Extract text content, removing HTML tags
    const textContent = tempDiv.textContent || tempDiv.innerText || '';

    // Clean up extra whitespace and line breaks
    return textContent
      .replace(/\s+/g, ' ') // Replace multiple whitespace with single space
      .trim();
  } catch (error) {
    console.error('Error extracting text from HTML:', error);
    // Fallback: try to extract text using regex if DOM parsing fails
    try {
      // Use a safer approach for HTML comment removal
      const doc = new DOMParser().parseFromString(html, 'text/html');
      
      // Remove script and style elements
      const scripts = doc.querySelectorAll('script, style');
      scripts.forEach(script => script.remove());
      
      // Get text content and clean up whitespace
      return doc.body.textContent
        ? doc.body.textContent
            .replace(/\s+/g, ' ') // Replace all whitespace sequences with a single space
            .trim()
        : '';
    } catch (regexError) {
      console.error('Error with regex fallback:', regexError);
      return html; // Return original if extraction fails
    }
  }
};
